<template lang="pug">
#resident-status-tab-component
  .register-body.mt-4.mb-4(v-show="0 < fields.length")
    ResidentStatusTable(
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        :filter="filter"
        :filterFields="filterFields")
    .table-options.d-flex.flex-row.justify-content-end.align-items-center
      // フィルタリング
      span.table-option-text 利用者グループ:
      .filter
        b-form-select(
          id="filter-input"
          v-model="filter"
          :options="filterOptions"
          size="md"
        )
      // ページあたりの行数
      span.table-option-text 1ページの行数:
      .per-page
        b-form-select(
          id="per-page-select"
          v-model="perPage"
          :options="pageOptions"
          size="md"
          @change="selectPerPage"
        )
      // ページネーション
      b-pagination.m-0(
        v-model="currentPage"
        :total-rows="rows"
        :per-page="perPage"
        :disabled="isLoading"
        @change="selectPage"
        hide-goto-end-buttons
      )

    .error-message(v-show="errorMessage")
      span {{ errorMessage }}

  .box-footer.register-footer
    ValidationAlert(v-model="showValidationAlert")
    .buttons.m-0
      button.btn.update-btn(@click="onUpdate(safetyConfirmsId, items)" type="button") 更新
      button.btn.cancel-btn(@click="onCancel" type="button") キャンセル
</template>

<script lang="ts">
import { Component, Inject, Prop, Watch, Vue } from "vue-property-decorator";
import { userStore, itemListFilterStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import SafetyConfirmResidentStatusesService, { SafetyConfirmResidentStatusGetModel } from "@/commons/services/SafetyConfirmResidentStatuses";
import FamilySafetyConfirmsTemplatesService, { FamilySafetyConfirmsTemplatesGetModel } from "@/commons/services/FamilySafetyConfirmsTemplates";
import FamilySafetyConfirmMastersService, {
  FamilySafetyConfirmMasterInputType,
  FamilySafetyConfirmMasterRadioInputOption,
  FamilySafetyConfirmMastersGetModel,
  FamilySafetyConfirmMasterType
} from "@/commons/services/FamilySafetyConfirmMasters";
import ResidentsService, { ResidentGetModel } from "@/commons/services/Residents";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import { errorHandle } from "@/errorHandler";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import ResidentStatusTable, { ResidentStatusTableField, ResidentStatusTableItem } from "@/pc/containers/safetyConfirms/ResidentStatusTable.vue";

/**
 * ### 利用者個別タブコンポーネント
 */
@Component({
  components: {
    ValidationAlert,
    ResidentStatusTable
  },
  $_veeValidate: { validator: "new" }
})
export default class ResidentStatusTab extends Vue {
  /** 安否確認(家族)利用者状況サービス */
  @Inject("SafetyConfirmResidentStatusesService") safetyConfirmResidentStatusesService!: SafetyConfirmResidentStatusesService;
  /** 安否確認(家族)テンプレートサービス */
  @Inject("FamilySafetyConfirmsTemplatesService") familySafetyConfirmsTemplatesService!: FamilySafetyConfirmsTemplatesService;
  /** 安否確認(家族)で送信する質問内容のマスタサービス */
  @Inject("FamilySafetyConfirmMastersService") familySafetyConfirmMastersService!: FamilySafetyConfirmMastersService;
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 災害ID */
  safetyConfirmsId: number = 0;
  /** 安否確認(家族)で送信する質問内容のマスタ */
  masters: Array<FamilySafetyConfirmMastersGetModel> = [];
  /** 安否確認(家族)テンプレート */
  template: FamilySafetyConfirmsTemplatesGetModel | null = null;
  /** 画面入力用の安否確認(家族)利用者状況 */
  statuses: Array<Status> = [];

  /** 利用者名 */
  fields: Array<ResidentStatusTableField> = [];
  /** 介護記録各行の情報 */
  items: Array<ResidentStatusTableItem> = [];
  /** 利用者グループMAP、key：利用者グループID value：利用者グループ名 */
  groupNameMap: Map<number, string> = new Map();

  /** 読込み中か否か */
  isLoading: boolean = true;
  /** フィルタリング条件 */
  filter: any = "";
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** フィルタリング対象カラム名 */
  filterFields: string[] = ["groupId"];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.residentStatus.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.residentStatus.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.residentStatus.pageRange;

  /** エラーメッセージ */
  @Prop({ required: true, type: String })
  errorMessage!: string;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `resident-status-tab-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### 画面情報を再作成する
   */
  public async refresh(): Promise<void> {
    this.items = [];
    this.currentRows = this.items.length;

    this.createView();

    return;
  }

  /**
   * ### vue lifecycle mounted
   *
   * - 1ページあたりの表示行数をCookieから取得する
   * - 画面表示で利用者のグループ名を使用するので、グループIDとグループ名の変換用にグループ情報のMAPを作成する
   * - フィルターの項目を作成する
   * - 画面表示情報を作成する
   */
  public async mounted(): Promise<void> {
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.careimage.perPage);

    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const groupNameMap: Map<number, string> = this.createGroupNameMap(groups);
    this.groupNameMap = groupNameMap;

    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(groups);
    this.filterOptions = filterOptions;
    // フィルタの復元
    this.restoreFilterSetting();

    // 画面情報の初期化
    this.items = [];
    this.currentRows = this.items.length;

    await this.createView();

    return;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = Number(cookieMap["resident-status-tab-per-page"]);

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### グループIDとグループ名のMAPを作成する
   *
   * - key：利用者グループID
   * - value：利用者グループ名
   */
  public createGroupNameMap(residentGroups: Array<ResidentGroupsGetModel>): Map<number, string> {
    let groupMap: Map<number, string> = new Map<number, string>();
    for (const group of residentGroups) {
      groupMap.set(group.id, group.name);
    }

    return groupMap;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - 検索用に利用者グループマスターのMAPを作る
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name];
    }
    return;
  }

  /**
   * ### 画面情報作成
   *
   * - テーブルで使用する全体の行数を非同期で取得する
   * - 利用者状況の質問項目の情報のために安否確認(家族)で送信する質問内容のマスタを取得する
   * - 利用者状況を入力する項目の表示判断のために安否確認(家族)テンプレートを取得する
   * - テンプレートから使用する質問項目のMAPを作成する
   * - 使用する質問項目のMAPから、テーブルの列名(field)を作成する
   * - 状況の入力対象は「家族ユーザーがいる利用者」なので、条件を満たす利用者を取得する
   * - 前回入力内容が存在する場合は前回入力値を復元するため、安否確認(家族)利用者状況を取得する
   * - 利用者状況の回答内容をMAP化する
   * - 利用者、質問内容のマスタ、使用する質問項目のMAP、回答内容のMAP、利用者グループ名のMAPからテーブルの行(items)を作成する
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    this.setRows();

    const safetyConfirmsId: number = Number(this.$route.params.id);
    this.safetyConfirmsId = safetyConfirmsId;

    const masters: Array<FamilySafetyConfirmMastersGetModel> = await this.getFamilySafetyConfirmMasters();
    this.masters = masters;

    const template: FamilySafetyConfirmsTemplatesGetModel = await this.getTemplate();
    const useIdMap: Map<number, boolean> = this.createUseIdMap(template);
    this.template = template;

    const fields: Array<ResidentStatusTableField> = this.creatFields(masters, useIdMap);
    this.fields = fields;

    const residents: Array<ResidentGetModel> = await this.getResidents();
    const residentStatuses: Array<SafetyConfirmResidentStatusGetModel> = await this.getResidentStatuses(safetyConfirmsId);
    const answerMap: Map<number, SafetyConfirmResidentStatusGetModel> = this.createAnswerMap(residentStatuses);
    const items: Array<ResidentStatusTableItem> = this.createItems(residents, masters, useIdMap, answerMap, this.groupNameMap);
    this.items = this.items.concat(items);
    this.currentRows = this.items.length;

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の家族がいる利用者の全件数を設定する
   */
  public async setRows() {
    try {
      const params: any = { existFamily: true };
      const count: number = await this.residentsService.getCount(userStore, params);
      this.rows = count;
    } catch (error) {
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 安否確認(家族)で送信する質問内容のマスタを取得する
   */
  public async getFamilySafetyConfirmMasters(): Promise<Array<FamilySafetyConfirmMastersGetModel>> {
    const response: Array<FamilySafetyConfirmMastersGetModel> = await this.familySafetyConfirmMastersService.get(userStore);
    return response;
  }

  /**
   * ### 安否確認(家族)テンプレートを取得する
   *
   * - 現時点ではテンプレートは1件のみなのでid指定は1固定
   */
  public async getTemplate(): Promise<FamilySafetyConfirmsTemplatesGetModel> {
    const response: FamilySafetyConfirmsTemplatesGetModel = await this.familySafetyConfirmsTemplatesService.get(userStore, 1);
    return response;
  }

  /**
   * ### テンプレートの使用する質問のMAPを作る
   *
   * - テンプレートの使用するIDリストに格納されている質問のみ使用する
   * - key：安否確認(家族)の質問ID
   * - value:true固定
   */
  public createUseIdMap(template: FamilySafetyConfirmsTemplatesGetModel): Map<number, boolean> {
    let map: Map<number, boolean> = new Map<number, boolean>();

    for (const useId of template.useIds) {
      map.set(useId, true);
    }

    return map;
  }

  /**
   * ### 列名を作成する
   *
   * - 固定の列名を追加する
   * - テンプレートの使用する質問を動的に列名を追加する
   *   - 動的な列名は質問マスタの情報から作成する
   *   - 列情報のkeyはマスタのname属性値を使用するが、HTMLの作法でケバブケースなのでvueの作法に則ってキャメルケースに変換して使用する
   */
  public creatFields(masters: Array<FamilySafetyConfirmMastersGetModel>, useIdMap: Map<number, boolean>): Array<ResidentStatusTableField> {
    let fields: Array<ResidentStatusTableField> = [];

    fields.push({ key: "groupName", label: "利用者グループ", sortable: false, sortDirection: "desc", class: "group-name", required: false });
    fields.push({ key: "residentRoomAndName", label: "部屋名・利用者名", sortable: false, sortDirection: "desc", class: "resident-room-and-name", required: false });

    for (const master of masters) {
      if (master.type != FamilySafetyConfirmMasterType.RESIDENT) {
        continue;
      }

      const use: boolean | undefined = useIdMap.get(master.id);
      if (use != undefined) {
        const columnName: string = this.kebabToCamelCase(master.name);
        const className: string = master.inputType == FamilySafetyConfirmMasterInputType.RADIO ? "radio-input" : "text-input";
        fields.push({ key: columnName, label: master.label, sortable: false, sortDirection: "desc", class: className, required: master.required });
      }
    }

    return fields;
  }

  /**
   * ### ケバブケースの値をキャメルケースに変換する
   */
  public kebabToCamelCase(kebab: string): string {
    return kebab
      .split("-")
      .map(function (word, index) {
        if (index === 0) {
          return word.toLowerCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join("");
  }

  /**
   * ### 利用者を取得する
   *
   * - 家族ユーザーが存在する利用者のみ取得する
   */
  public async getResidents(): Promise<Array<ResidentGetModel>> {
    const startIndex: number = this.currentRows;
    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象のよりも多めに取得しておく
    const endIndex: number = this.perPage * (this.currentPage * this.pageRange);
    const params: any = {
      startIndex: startIndex,
      endIndex: endIndex,
      existFamily: true
    };
    const response: Array<ResidentGetModel> = await this.residentsService.gets(userStore, params);

    return response;
  }

  /**
   * ### 安否確認(家族)の利用者状況を取得する
   */
  public async getResidentStatuses(safetyConfirmsId: number): Promise<Array<SafetyConfirmResidentStatusGetModel>> {
    const response: Array<SafetyConfirmResidentStatusGetModel> = await this.safetyConfirmResidentStatusesService.gets(userStore, safetyConfirmsId);
    return response;
  }

  /**
   * ### 安否確認(家族)の利用者状況の回答をMAP化する
   *
   * - key:利用者ID
   * - value：利用者状況
   *
   */
  public createAnswerMap(residentStatuses: Array<SafetyConfirmResidentStatusGetModel>): Map<number, SafetyConfirmResidentStatusGetModel> {
    let answerMap: Map<number, SafetyConfirmResidentStatusGetModel> = new Map<number, SafetyConfirmResidentStatusGetModel>();

    for (const status of residentStatuses) {
      answerMap.set(status.residentId, status);
    }

    return answerMap;
  }

  /**
   * ### テーブル行情報を作成する
   */
  public createItems(
    residents: Array<ResidentGetModel>,
    masters: Array<FamilySafetyConfirmMastersGetModel>,
    useIdMap: Map<number, boolean>,
    answerMap: Map<number, SafetyConfirmResidentStatusGetModel>,
    groupNameMap: Map<number, string>
  ): Array<ResidentStatusTableItem> {
    let items: Array<ResidentStatusTableItem> = [];

    for (const resident of residents) {
      const groupName: string | undefined = groupNameMap.get(resident.groupId);
      const residentStatus: SafetyConfirmResidentStatusGetModel | undefined = answerMap.get(resident.id);
      const statuses: any = this.createResidentStatus(masters, useIdMap, residentStatus);

      items.push({
        facilityId: resident.facilityId,
        groupId: resident.groupId,
        groupName: groupName != undefined ? groupName : "",
        room: resident.room,
        residentId: resident.id,
        residentName: resident.name,
        status: statuses,
        updated: residentStatus != undefined ? residentStatus.updated : undefined
      });
    }

    return items;
  }

  /**
   * ### 入力させる使用する質問リストを作成する
   *
   * - 既に入力されている情報がある場合は値を格納し、ない場合は空値を格納する
   * - 入力種別がラジオボタンの質問は選択肢ラベルを作成する
   * - テンプレート情報から質問を使用するかしないかを設定する
   * - 行情報のkeyはマスタのname属性値を使用するが、HTMLの作法でケバブケースなのでvueの作法に則ってキャメルケースに変換して使用する
   */
  public createResidentStatus(
    masters: Array<FamilySafetyConfirmMastersGetModel>,
    useIdMap: Map<number, boolean>,
    residentStatus: SafetyConfirmResidentStatusGetModel | undefined
  ): any {
    let statuses: any = {};

    let answerMap: Map<number, string> = new Map<number, string>();
    if (residentStatus != undefined) {
      for (const status of residentStatus.statuses) {
        answerMap.set(status.id, status.answer);
      }
    }

    for (const master of masters) {
      if (master.type != FamilySafetyConfirmMasterType.RESIDENT) {
        continue;
      }

      const use: boolean | undefined = useIdMap.get(master.id);
      if (use != undefined) {
        let options: Array<{ value: number | string; label: string }> = [];

        // 入力種別がラジオボタンの場合は選択肢を作成する
        if (master.inputType == FamilySafetyConfirmMasterInputType.RADIO) {
          const inputOptions: FamilySafetyConfirmMasterRadioInputOption = master.inputOptions as FamilySafetyConfirmMasterRadioInputOption;
          options = inputOptions.choices;
        }

        // 既に入力されている場合はその値を使用する
        let answer: string | undefined = answerMap.get(master.id);

        const status: Status = {
          ...master,
          answer: answer != undefined ? answer : "",
          options: options
        };

        const name: string = this.kebabToCamelCase(status.name);
        statuses[`${name}`] = status;
      }
    }

    return statuses;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.items = [];
    this.currentRows = this.items.length;
    await this.createView();

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    // 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }

  /**
   * ### 更新ボタン押下イベント
   */
  public async onUpdate(safetyConfirmsId: number, statuses: Array<ResidentStatusTableItem>): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult == true) {
      this.showValidationAlert = false;
      const model: ResidentStatusTabOnUpdateModel = {
        safetyConfirmsId: safetyConfirmsId,
        updateStatuses: statuses
      };
      this.$emit("onUpdate", model);
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### キャンセルボタン押下イベント
   */
  public onCancel(): void {
    this.$emit("onCancel");
    return;
  }

  setFilterSetting() {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name, filter: this.filter });
    }
    return;
  }
}

export interface Status extends FamilySafetyConfirmMastersGetModel {
  /** 回答内容 */
  answer: string;
  /** ラジオボタンの場合の選択肢 */
  options?: Array<{ value: number | string; label: string }>;
}

export interface ResidentStatusTabOnUpdateModel {
  /** 災害ID */
  safetyConfirmsId: number;
  /** 利用者状況 */
  updateStatuses: Array<ResidentStatusTableItem>;
}
</script>

<style lang="scss" scoped>
#resident-status-tab-component {
  .update-btn,
  .cancel-btn {
    width: 120px;
  }

  .box-footer {
    padding: 10px 0 0 0 !important;
  }
}
</style>
